import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, User, Calendar, Shield, FileText, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { warrantyService } from '../lib/warrantyService';
import { SoldProduct, WarrantyClaim } from '../types';
import { formatDate } from '../lib/dateUtils';

export default function WarrantyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [soldProduct, setSoldProduct] = useState<SoldProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [showClaimForm, setShowClaimForm] = useState(false);

  useEffect(() => {
    if (id) {
      loadSoldProduct();
    }

    // Auto-refresh when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && id) {
        loadSoldProduct();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [id]);

  const loadSoldProduct = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const product = await warrantyService.getSoldProductById(id);
      setSoldProduct(product);
    } catch (error) {
      console.error('Error loading sold product:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWarrantyStatus = () => {
    if (!soldProduct) return 'unknown';
    const now = new Date();
    const warrantyEnd = new Date(soldProduct.warranty_end_date);
    return warrantyEnd >= now ? 'active' : 'expired';
  };

  const getDaysRemaining = () => {
    if (!soldProduct) return 0;
    const now = new Date();
    const warrantyEnd = new Date(soldProduct.warranty_end_date);
    const diff = warrantyEnd.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const handleDeleteClaim = async (claimId: string) => {
    if (!window.confirm('Are you sure you want to delete this warranty claim?')) return;

    try {
      await warrantyService.deleteWarrantyClaim(claimId);
      loadSoldProduct();
    } catch (error) {
      console.error('Error deleting warranty claim:', error);
      alert('Failed to delete warranty claim');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading warranty details...</p>
        </div>
      </div>
    );
  }

  if (!soldProduct) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Warranty Not Found</h2>
        <p className="text-slate-600 mb-6">The warranty record you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/warranties')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Warranties
        </Button>
      </div>
    );
  }

  const warrantyStatus = getWarrantyStatus();
  const daysRemaining = getDaysRemaining();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/warranties')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Warranty Details</h1>
          <p className="text-slate-600 font-mono">{soldProduct.serial_number}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-slate-600" />
              <h2 className="text-xl font-semibold text-slate-900">Product Information</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-600">Product Name</p>
                <p className="font-medium text-slate-900">
                  {soldProduct.manufactured_product?.name || soldProduct.supply?.name || 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm text-slate-600">Product Type</p>
                <Badge variant="secondary" className="mt-1">
                  {soldProduct.product_type}
                </Badge>
              </div>

              <div>
                <p className="text-sm text-slate-600">Serial Number</p>
                <p className="font-mono text-sm text-slate-900">{soldProduct.serial_number}</p>
              </div>

              <div>
                <p className="text-sm text-slate-600">Condition</p>
                <p className="font-medium text-slate-900">{soldProduct.condition}</p>
              </div>

              {soldProduct.notes && (
                <div className="col-span-2">
                  <p className="text-sm text-slate-600">Notes</p>
                  <p className="text-slate-900">{soldProduct.notes}</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-slate-600" />
              <h2 className="text-xl font-semibold text-slate-900">Customer Information</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-600">Customer Name</p>
                <p className="font-medium text-slate-900">{soldProduct.customer_name}</p>
              </div>

              <div>
                <p className="text-sm text-slate-600">Order Date</p>
                <p className="font-medium text-slate-900">{formatDate(soldProduct.sale_date)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-slate-600" />
              <h2 className="text-xl font-semibold text-slate-900">Supply Traceability</h2>
            </div>

            {soldProduct.supplies_used && soldProduct.supplies_used.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-700 border-b border-slate-600">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                        Supply Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                        Lot Number
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                        Quantity Used
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {soldProduct.supplies_used.map((supply) => (
                      <tr key={supply.id}>
                        <td className="px-4 py-3 text-sm text-slate-900">{supply.supply_name}</td>
                        <td className="px-4 py-3 text-sm font-mono text-slate-600">
                          {supply.lot_number || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {supply.quantity_used} {supply.unit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-600">No supply traceability data available</p>
            )}
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-slate-600" />
                <h2 className="text-xl font-semibold text-slate-900">Warranty Claims</h2>
              </div>
              <Button size="sm" onClick={() => setShowClaimForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Claim
              </Button>
            </div>

            {soldProduct.warranty_claims && soldProduct.warranty_claims.length > 0 ? (
              <div className="space-y-4">
                {soldProduct.warranty_claims.map((claim) => (
                  <div key={claim.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm font-medium text-slate-900">
                            {claim.claim_number}
                          </p>
                          <Badge
                            variant={
                              claim.status === 'completed' ? 'success' :
                              claim.status === 'rejected' ? 'danger' :
                              claim.status === 'in_progress' ? 'primary' :
                              'warning'
                            }
                          >
                            {claim.status}
                          </Badge>
                          <Badge variant="secondary">{claim.claim_type}</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Filed on {formatDate(claim.claim_date)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDeleteClaim(claim.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {claim.supply_name && (
                        <div>
                          <p className="text-sm font-medium text-slate-700">Component</p>
                          <p className="text-sm text-slate-600">{claim.supply_name}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-700">Issue Description</p>
                        <p className="text-sm text-slate-600">{claim.issue_description}</p>
                      </div>

                      {claim.resolution_notes && (
                        <div>
                          <p className="text-sm font-medium text-slate-700">Resolution</p>
                          <p className="text-sm text-slate-600">{claim.resolution_notes}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-4 text-sm">
                        {claim.cost > 0 && (
                          <span className="text-slate-600">
                            Cost: <span className="font-medium">${claim.cost.toFixed(2)}</span>
                          </span>
                        )}
                        {claim.handled_by && (
                          <span className="text-slate-600">
                            Handled by: <span className="font-medium">{claim.handled_by}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-600">No warranty claims filed</p>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-slate-600" />
              <h2 className="text-xl font-semibold text-slate-900">Warranty Status</h2>
            </div>

            <div className="space-y-4">
              <div className="text-center p-4 rounded-lg" style={{
                backgroundColor: warrantyStatus === 'active' ? '#dcfce7' : '#991b1b'
              }}>
                <p className="text-sm mb-1" style={{
                  color: warrantyStatus === 'active' ? '#166534' : '#fecaca'
                }}>Status</p>
                <p className="text-2xl font-bold" style={{
                  color: warrantyStatus === 'active' ? '#16a34a' : '#ffffff'
                }}>
                  {warrantyStatus === 'active' ? 'Active' : 'Expired'}
                </p>
              </div>

              {warrantyStatus === 'active' && (
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Days Remaining</p>
                  <p className="text-2xl font-bold text-blue-600">{daysRemaining}</p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Start Date (Delivery)</span>
                  <span className="font-medium text-slate-900">
                    {formatDate(soldProduct.warranty_start_date)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">End Date</span>
                  <span className="font-medium text-slate-900">
                    {formatDate(soldProduct.warranty_end_date)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Period</span>
                  <span className="font-medium text-slate-900">
                    {soldProduct.warranty_period_months} months
                  </span>
                </div>
              </div>

              <div className="text-center p-4 bg-red-600 rounded-lg">
                <p className="text-sm text-red-100 mb-1">Denied Claims</p>
                <p className="text-2xl font-bold text-white">
                  {soldProduct.warranty_claims?.filter(claim => claim.status === 'rejected').length || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {showClaimForm && (
        <WarrantyClaimForm
          soldProductId={soldProduct.id}
          onClose={() => setShowClaimForm(false)}
          onSuccess={() => {
            setShowClaimForm(false);
            loadSoldProduct();
          }}
        />
      )}
    </div>
  );
}

interface WarrantyClaimFormProps {
  soldProductId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function WarrantyClaimForm({ soldProductId, onClose, onSuccess }: WarrantyClaimFormProps) {
  const [formData, setFormData] = useState({
    claim_type: 'repair' as 'repair' | 'replacement' | 'refund' | 'denied',
    issue_description: '',
    handled_by: '',
    cost: 0,
    supply_id: '',
    supply_name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [soldProduct, setSoldProduct] = useState<SoldProduct | null>(null);

  useEffect(() => {
    loadSoldProduct();
  }, [soldProductId]);

  const loadSoldProduct = async () => {
    try {
      const product = await warrantyService.getSoldProductById(soldProductId);
      setSoldProduct(product);
    } catch (error) {
      console.error('Error loading sold product:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.issue_description.trim()) {
      alert('Please provide an issue description');
      return;
    }

    try {
      setSubmitting(true);
      await warrantyService.createWarrantyClaim({
        sold_product_id: soldProductId,
        supply_id: formData.supply_id || undefined,
        supply_name: formData.supply_name || undefined,
        claim_type: formData.claim_type,
        issue_description: formData.issue_description,
        handled_by: formData.handled_by || undefined,
        cost: formData.cost,
        status: 'pending',
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating warranty claim:', error);
      alert('Failed to create warranty claim');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900">New Warranty Claim</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {soldProduct?.supplies_used && soldProduct.supplies_used.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Component (Optional)
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.supply_id}
                  onChange={(e) => {
                    const selectedSupply = soldProduct.supplies_used?.find(s => s.supply_id === e.target.value);
                    setFormData({
                      ...formData,
                      supply_id: e.target.value,
                      supply_name: selectedSupply?.supply_name || '',
                    });
                  }}
                >
                  <option value="">Entire Product</option>
                  {soldProduct.supplies_used.map((supply) => (
                    <option key={supply.id} value={supply.supply_id || ''}>
                      {supply.supply_name}
                      {supply.lot_number && ` (Lot: ${supply.lot_number})`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Select a specific component if the issue is isolated to one part
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Claim Type *
              </label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.claim_type}
                onChange={(e) => setFormData({ ...formData, claim_type: e.target.value as any })}
                required
              >
                <option value="repair">Repair</option>
                <option value="replacement">Replacement</option>
                <option value="refund">Refund</option>
                <option value="denied">Denied</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Issue Description *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                value={formData.issue_description}
                onChange={(e) => setFormData({ ...formData, issue_description: e.target.value })}
                placeholder="Describe the issue..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Handled By
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.handled_by}
                onChange={(e) => setFormData({ ...formData, handled_by: e.target.value })}
                placeholder="Person handling the claim"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Estimated Cost
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Creating...' : 'Create Claim'}
              </Button>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
